import {
    INavigationManager,
    ROOT_CHANGING_EVENT
} from '@kta20-core/interfaces/i-navigation-manager';
import { NavigationEventHandler } from '@kta20-core/interfaces/i-navigation-options';
import { NavigationOptions } from '@kta20-core/navigation-options';
import { ViewName } from '@kta20-core/interfaces/view-name';
import * as Constants from '@kta20-core/constants';
import {OfflineError} from "@kta20-core/errors/offline-error";
import {Translation} from "@kta20-ui-base/resources/lang/en";
import {NetworkService} from "@kta20-core/net/network-service";

class NavigationManager implements INavigationManager{

    private static _inst: NavigationManager;
    private _currentNavHostName: string;
    private _navHosts: Map<string, any> = new Map<string, any>();
    private _didChangeEventName: string;
    private _willChangeEventName: string;
    private _viewsNames: ViewName[] = [];
    private _networkService:NetworkService;
    private tr:Translation;

    constructor() {
        if (NavigationManager._inst) throw new Error(Constants.SINGLETON_CLASS);
    }

    /**
     * Params required the first init only
     * @param {Translation} tr
     * @param networkService
     */
    static get(tr?:Translation,networkService?:NetworkService): NavigationManager {
        if (!this._inst) {
            this._inst = new NavigationManager();
            this._inst.tr = tr;
            this._inst._networkService = networkService;
        }

        return this._inst;
    }

    canGoBack(): boolean {
        let result:boolean = false;

        (async ()=>{
            const viewsIntoNav = this.getNavHost().children;
            result = viewsIntoNav ? (viewsIntoNav.length > 1) : false;
        })();

        return result;
    }

    alertOnLeave(enabled:boolean){
        if(enabled)
            window.onbeforeunload = (e) => {
                e.preventDefault();
                e.returnValue = 'Do you want to leave the app?'; //most common browsers do override this message
            };
        else
            window.onbeforeunload = null;
    }

    addViews(viewNames: ViewName[]) {
        this._viewsNames.push(...viewNames);
    }

    async loadView(name: string, onLoaded?: () => void): Promise<void> {
        console.log('NavHost in use: ' + this._currentNavHostName);

        const viewName = this._viewsNames.find(vn => vn.name == name);

        //todo: fire a "loading view" event

        if(!viewName) throw new Error(`The view "${name}" is not registered into the NavigationManager instance. Please register it before invoking, by ".addViews()" method.`);

        if (!viewName.loaded) {
            if(!this._networkService.hasNetwork())
                throw new OfflineError(this.tr.cannot_navigate_due_to_missing_network_connection);

            await viewName.loader();
            viewName.loaded = true;
        }

        //todo: fire a "view loaded" event
        if (onLoaded) onLoaded();
    }

    addNavHost(id: string, navHost: any): INavigationManager {
        this._navHosts.set(id, navHost);
        return this;
    };

    removeNavHost(id: string): INavigationManager {
        this._navHosts.delete(id);
        return this;
    };

    setEventNames(didChangeEventName: string, willChangeEventName: string): INavigationManager {
        this._didChangeEventName = didChangeEventName;
        this._willChangeEventName = willChangeEventName;
        return this;
    }

    useNavHost(id: string): INavigationManager {
        if (!this._navHosts.has(id))
            throw new Error(`No navHost added for name ${id}. Please call .addNavHost() before to register and then use it.`);

        this._currentNavHostName = id;
        return this;
    };

    getNavHost(): any {
        return this._navHosts.get(this._currentNavHostName);
    }

    async getActive(): Promise<Object> {
        try { //optimistic approach
            return await this.getNavHost().getActive();
        }
        catch (err) {
            console.warn(err);
            return {};
        }
    }

    async setRoot(opts: NavigationOptions): Promise<void> {
        await this.loadView(opts.viewName, async () => {

            this.getNavHost().dispatchEvent(new CustomEvent(ROOT_CHANGING_EVENT, {
                detail:{
                    navHostName:this._currentNavHostName,
                    viewName:opts.viewName
                }
            }));

            await this.getNavHost().setRoot(opts.viewName, opts.data);
        });
    }

    async push(opts: NavigationOptions): Promise<void> {
        await this.loadView(opts.viewName, async () => {
            await this.getNavHost().push(opts.viewName, opts.data);
        });
    }

    async pop(opts?: NavigationOptions, done?: NavigationEventHandler): Promise<boolean> {
        let result: boolean;
        if (opts)
            result = this.getNavHost().pop(
                opts.viewName,
                opts.data,
                done || null,
            );
        else
            result = this.getNavHost().pop(null, done || null);

        return result;
    }

    onViewWillChange(handler: NavigationEventHandler, viewName: string) {
        this.getNavHost().addEventListener(
            this.getWillChangeEventName(),
            ()=>this.onViewChangeWrapper(this.getWillChangeEventName(), viewName, handler)
        );
    };

    onViewDidChange(handler: NavigationEventHandler, viewName: string) {
        this.getNavHost().addEventListener(
            this.getDidChangeEventName(),
            ()=>this.onViewChangeWrapper(this.getDidChangeEventName(), viewName, handler)
        );
    };

    offViewWillChange(handler: NavigationEventHandler, viewName: string) {
        this.getNavHost().removeEventListener(
            this.getWillChangeEventName(),
            ()=>this.onViewChangeWrapper(this.getWillChangeEventName(), viewName, handler)
        );
    };

    offViewDidChange(handler: NavigationEventHandler, viewName: string) {
        this.getNavHost().removeEventListener(
            this.getDidChangeEventName(),
            ()=>this.onViewChangeWrapper(this.getDidChangeEventName(), viewName, handler)
        );
    };

    on(eventName: string, handler: NavigationEventHandler): void {
        this.getNavHost().addEventListener(eventName, handler);
    };

    off(eventName: string, handler: NavigationEventHandler): void {
        this.getNavHost().removeEventListener(eventName, handler);
    };

    getDidChangeEventName(): string {
        return this._didChangeEventName;
    }

    getWillChangeEventName(): string {
        return this._willChangeEventName;
    }

    private async onViewChangeWrapper(eventName: string, viewName: string, handler: NavigationEventHandler) {
        const actView = await this.getActive();
        if(actView && (viewName==='*' || (<any>actView).component===viewName))
            handler(new CustomEvent(eventName));
    }

}

export {NavigationManager as NavigationManager};